import { useState } from "react";
import { Link } from "react-router";
import { Link2, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { ListaDeReferencias } from "@/components/referencias/ListaDeReferencias";
import { ModalDeCorreccion } from "@/components/referencias/ModalDeCorreccion";
import { ResumenDeReferencias } from "@/components/referencias/ResumenDeReferencias";
import { Button } from "@/components/ui/button";
import { useHomologacion } from "@/hooks/useHomologacion";
import {
  useAjustarReferencia,
  useCorregirComparable,
  useDescartarReferencia,
  usePapeleraDeReferencias,
  useReferenciasDelInforme,
  useValidarReferencia,
} from "@/hooks/useReferencias";
import { getApiErrorMessage } from "@/lib/api";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import { type ClaseDeEvidencia, type ReferenciaDelInforme } from "@/lib/referencias";

/**
 * Qué decir cuando el informe todavía no enlazó nada de esta clase.
 *
 * Se nombra la FUENTE y no la pantalla: "sin transacciones" a secas se lee como
 * que el predio no tuvo movimiento, cuando lo que pasa es que nadie enlazó
 * ninguna todavía.
 */
const VACIO: Record<ClaseDeEvidencia, { titulo: string; descripcion: string }> = {
  transaccion: {
    titulo: "Sin transacciones del CBR",
    descripcion:
      "Las inscripciones del Conservador que se enlacen a este informe aparecen acá, con lo que valían al enlazarlas.",
  },
  oferta: {
    titulo: "Sin ofertas de mercado",
    descripcion:
      "Los avisos de portales que se enlacen a este informe aparecen acá, con lo que pedían al enlazarlas.",
  },
};

interface PestanaDeReferenciasProps {
  informeId: number;
  clase: ClaseDeEvidencia;
  /**
   * La superficie del predio TASADO, ya en hectáreas, de la que sale el valor
   * total del panel. La convierte quien la pasa (`superficieEnHectareas`): acá
   * llega lista, para que esta pantalla no tenga que conocer la unidad del
   * informe.
   */
  superficieDelPredioHa: number | null;
}

/**
 * Lo que este informe se llevó del mercado, partido por clase de evidencia.
 *
 * ES LA PUERTA QUE FALTABA. Hasta el 08-09-2026 lo enlazado solo se veía desde
 * adentro del propio módulo de referencias —el enlace del panel de selección y
 * el de la papelera—, así que quien miraba la ficha del informe no tenía cómo
 * llegar y el trabajo parecía no haberse guardado en ninguna parte.
 *
 * SON DOS PESTAÑAS Y NO UNA, al revés que la pantalla de enlazadas, que las
 * muestra juntas. Una inscripción del Conservador es un precio PAGADO y un
 * aviso de portal es un precio PEDIDO: mezclarlos en una sola lista obliga a
 * leer el badge de cada tarjeta para saber cuál se está mirando, que es
 * justamente la distinción que el tasador necesita tener a la vista.
 *
 * UNA SOLA CONSULTA ALIMENTA LAS DOS. El listado del informe no se filtra por
 * clase en el servidor: el conjunto es chico —lo que una persona eligió a mano—
 * y pedirlo dos veces serían dos viajes para los mismos datos. La caché de
 * TanStack Query lo comparte además con el contador de las pestañas, que vive
 * en la pantalla de arriba.
 */
export function PestanaDeReferencias({
  informeId,
  clase,
  superficieDelPredioHa,
}: PestanaDeReferenciasProps) {
  const enlazadas = useReferenciasDelInforme(informeId);
  const papelera = usePapeleraDeReferencias(informeId);

  const validar = useValidarReferencia(informeId);
  const descartar = useDescartarReferencia(informeId);
  const ajustar = useAjustarReferencia(informeId);
  const corregir = useCorregirComparable(informeId);

  /* CUÁL se está editando, y no un booleano: el modal necesita la ficha entera
     para llenar el formulario. Guardar el id obligaría a buscarla otra vez en
     una lista que la mutación acaba de reemplazar. */
  const [editando, setEditando] = useState<ReferenciaDelInforme | null>(null);

  /* LA ESCALA VIVE EN OTRA CONSULTA, y su fracaso NO se propaga a la lista: una
     ficha que desaparece porque no se pudo cargar un selector esconde lo que el
     informe SÍ tiene guardado. Sin escala, `AjustesDeHomologacion` no dibuja
     nada y el resto de la tarjeta se ve igual. */
  const homologacion = useHomologacion();

  const estado = estadoDeConsulta(enlazadas);
  const referencias = (enlazadas.data?.referencias ?? []).filter((r) => r.clase === clase);

  /* CUENTA LAS DOS CLASES, al revés que la lista de arriba: la papelera es una
     sola y un número por clase mentiría sobre cuántas fichas hay adentro. Es la
     misma regla que sigue el `meta` del catálogo. */
  const enLaPapelera = papelera.data?.total ?? 0;

  const ocupada = validar.isPending || descartar.isPending || ajustar.isPending;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-ink-muted text-caption max-w-2xl">
          {clase === "transaccion"
            ? "Inscripciones del Conservador de Bienes Raíces que ya son de este informe: precios pagados, congelados al enlazarlos."
            : "Avisos de portales que ya son de este informe: precios pedidos, congelados al enlazarlos."}{" "}
          Descartar una la saca de este informe y no del catálogo, que sigue disponible para
          cualquier otra tasación.
        </p>

        <div className="flex items-center gap-3">
          {/* SE OCULTA ENTERA CUANDO NO HAY NADA DESCARTADO, no se deshabilita:
              una papelera vacía no es una acción bloqueada, es una puerta que
              todavía no existe. Va como enlace y no como botón porque sale a
              otra pantalla; el botón sólido queda para lo que se vino a hacer. */}
          {enLaPapelera > 0 && (
            <Link
              to={`/referencias/${informeId}/enlazadas?vista=papelera`}
              className="text-ink-muted hover:text-danger-soft-foreground decoration-border hover:decoration-danger-soft-foreground/40 inline-flex items-center gap-1.5 text-xs font-bold underline underline-offset-[3px] transition-colors"
            >
              <Trash2 className="size-3.5" />
              Papelera ({enLaPapelera})
            </Link>
          )}

          {/* Mismo ícono de eslabón que el menú reserva para enlazar, y el mismo
              destino: el catálogo de mercado de ESTE informe. */}
          <Button asChild>
            <Link to={`/referencias/${informeId}`}>
              <Link2 className="size-4" />
              Enlazar referencias
            </Link>
          </Button>
        </div>
      </div>

      {/* DOS COLUMNAS, como el paso 2 del enlazador: el resumen acompaña a la
          lista en vez de quedar al final, donde habría que bajar toda la
          pestaña para ver la cifra que resume lo que se está corrigiendo.
          Abajo de `lg` cae debajo, que es lo único que entra. */}
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_19.5rem]">
      <ListaDeReferencias
        estado={estado}
        referencias={referencias}
        que={
          clase === "transaccion"
            ? "las transacciones del CBR de este informe"
            : "las ofertas de mercado de este informe"
        }
        tituloDeError="No se pudieron cargar las referencias de este informe"
        error={enlazadas.error}
        onReintentar={enlazadas.refetch}
        vacio={VACIO[clase]}
        ocupada={ocupada}
        onValidar={(referencia, validada) => validar.mutate({ id: referencia.id, validada })}
        onDescartar={(referencia) => descartar.mutate(referencia.id)}
        escalaDeHomologacion={homologacion.data}
        onAjustar={(referencia, categoriaIds) =>
          ajustar.mutate({ id: referencia.id, categoriaIds })
        }
        onEditar={setEditando}
      />

      {/* ACOMPAÑA AL SCROLL. Es la cifra contra la que se mira cada ajuste que
          se elige más abajo: quieta arriba, con diez fichas deja de estar a la
          vista justo cuando se la necesita.

          Y LLEVA TOPE DE ALTO CON SCROLL PROPIO, igual que el panel del
          enlazador y por lo mismo: desde que el desglose lista una fila por
          ficha, el panel crece con el informe, y pegado sin tope lo que sobra
          queda debajo del borde para siempre — el aviso de "sin homologar" es
          justamente lo último.

          `100cqh` es el alto de `main`, que se declara contenedor de tamaño en
          `MainLayout` para esto; `100svh` se pasaría por lo que mide la barra
          superior. Se le restan el `top-5` y el mismo aire abajo. Todo con
          `lg:` porque en pantallas angostas el panel cae debajo de la lista y
          ahí un tope lo encerraría en una caja chica sin ningún motivo. */}
      <div className="scroll-fino lg:sticky lg:top-5 lg:max-h-[calc(100cqh-2.5rem)] lg:overflow-y-auto">
        <ResumenDeReferencias
          referencias={referencias}
          escala={homologacion.data ?? []}
          clase={clase}
          superficieDelPredioHa={superficieDelPredioHa}
        />
      </div>
      </div>

      {/* EL MODAL VIVE ACÁ Y NO DENTRO DE LA FICHA: uno por pestaña, no uno por
          comparable. Con diez fichas enlazadas habría diez diálogos montados,
          cada uno con su formulario y su selector de comunas, para que se use
          uno a la vez. */}
      <ModalDeCorreccion
        referencia={editando}
        abierto={editando !== null}
        onCerrar={() => setEditando(null)}
        guardando={corregir.isPending}
        onGuardar={(correccion) => {
          if (!editando) return;

          corregir.mutate(
            { id: editando.id, correccion },
            {
              // SE CIERRA AL CONFIRMAR EL SERVIDOR, no al apretar. Cerrando
              // antes, un rechazo de validación dejaría el formulario perdido y
              // a la persona creyendo que guardó.
              onSuccess: () => {
                setEditando(null);
                toast.success("Comparable corregido");
              },
              onError: (error) =>
                toast.error(getApiErrorMessage(error, "No se pudo guardar la corrección")),
            },
          );
        }}
      />
    </div>
  );
}
